import { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { pollTask } from "../lib/tasks";
import { fmtDate } from "../lib/format";
import { toast } from "sonner";
import { Globe, UploadSimple, Sparkle, MagnifyingGlass, ArrowRight, Trash, ChartBar, GitDiff, Check } from "@phosphor-icons/react";

export default function Observatory() {
  const [taxo, setTaxo] = useState(null);
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ organisation: "", pays: "", annee: "", theme: "", type_rapport: "" });
  const [showUpload, setShowUpload] = useState(false);
  const [selected, setSelected] = useState(new Set());

  useEffect(() => {
    api.get("/observatory/taxonomy").then((r) => setTaxo(r.data));
  }, []);

  useEffect(() => { load(); }, [filters]);

  const load = async () => {
    setLoading(true);
    try {
      const params = Object.fromEntries(Object.entries(filters).filter(([_, v]) => v));
      const r = await api.get("/observatory/reports", { params });
      setReports(r.data);
    } catch (e) { toast.error("Erreur chargement"); }
    finally { setLoading(false); }
  };

  const remove = async (id) => {
    if (!confirm("Supprimer ce rapport ?")) return;
    await api.delete(`/observatory/reports/${id}`);
    toast.success("Rapport supprimé");
    load();
  };

  const analyze = async (id) => {
    try {
      const r = await api.post(`/observatory/reports/${id}/analyze`);
      toast.info("Analyse IA en cours — patientez ~1 min");
      const final = await pollTask(r.data.task_id, { intervalMs: 2500, timeoutMs: 180000 });
      if (final.status === "error") throw new Error(final.error);
      toast.success("Analyse terminée");
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || err.message || "Erreur IA"); }
  };

  const toggleSelect = (id) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id); else next.add(id);
    setSelected(next);
  };

  const compare = async () => {
    if (selected.size < 2) return toast.error("Sélectionnez au moins 2 rapports analysés");
    try {
      const r = await api.post("/observatory/compare", {
        report_ids: Array.from(selected),
        focus: filters.theme || "dette",
      });
      toast.info("Comparaison IA en cours...");
      const final = await pollTask(r.data.task_id, { intervalMs: 2500, timeoutMs: 180000 });
      if (final.status === "error") throw new Error(final.error);
      window.location.href = `/observatory/compare/${final.result_id}`;
    } catch (err) { toast.error(err?.response?.data?.detail || err.message || "Erreur"); }
  };

  if (!taxo) return <div className="p-12 label-mono">Chargement de la taxonomie...</div>;

  return (
    <div className="max-w-[1400px] mx-auto px-4 sm:px-8 py-10">
      <div className="mb-8 fade-up flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="label-mono">§ 07 · Vita-Observatory</div>
          <h1 className="mt-2 font-serif text-4xl sm:text-5xl font-medium tracking-tight">Veille Internationale</h1>
          <p className="mt-3 text-[var(--muted)] max-w-2xl">Terminal d'analyse des rapports flagship du FMI, BM, BAD, CEA, UNCTAD et autres organisations. Importez, analysez, comparez.</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Link to="/observatory/dashboard" data-testid="obs-dashboard-link"
            className="border border-black px-4 py-2 label-mono hover:bg-black hover:text-[var(--paper)] flex items-center gap-2">
            <ChartBar size={14} /> Vulnérabilité
          </Link>
          <Link to="/observatory/data" data-testid="obs-data-link"
            className="border border-black px-4 py-2 label-mono hover:bg-black hover:text-[var(--paper)] flex items-center gap-2">
            <Globe size={14} /> Sources officielles
          </Link>
          <button onClick={compare} disabled={selected.size < 2} data-testid="obs-compare-btn"
            className="bg-[var(--ink)] text-[var(--paper)] px-4 py-2 label-mono hover:bg-[var(--signal)] flex items-center gap-2 disabled:opacity-40">
            <GitDiff size={14} /> Comparer ({selected.size})
          </button>
          <button onClick={() => setShowUpload(true)} data-testid="obs-upload-btn"
            className="bg-[var(--signal)] hover:bg-[var(--signal-hover)] text-[var(--paper)] px-4 py-2 label-mono flex items-center gap-2">
            <UploadSimple size={14} weight="bold" /> Importer un rapport
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="border border-black/15 bg-[var(--paper)] p-4 mb-6 grid grid-cols-2 md:grid-cols-5 gap-3">
        <Filter label="Organisation" value={filters.organisation} options={Object.entries(taxo.organisations).map(([k,v]) => [k, v.label])} onChange={(v) => setFilters({...filters, organisation: v})} testid="filter-org" />
        <Filter label="Pays / Région" value={filters.pays} options={taxo.pays.map((p) => [p.code, p.nom])} onChange={(v) => setFilters({...filters, pays: v})} testid="filter-pays" />
        <Filter label="Type" value={filters.type_rapport} options={Object.entries(taxo.types_rapport)} onChange={(v) => setFilters({...filters, type_rapport: v})} testid="filter-type" />
        <Filter label="Thème" value={filters.theme} options={taxo.themes.map((t) => [t.code, t.label])} onChange={(v) => setFilters({...filters, theme: v})} testid="filter-theme" />
        <div>
          <div className="label-mono mb-1">Année</div>
          <input type="number" value={filters.annee} onChange={(e) => setFilters({...filters, annee: e.target.value})}
            className="w-full border border-black/30 px-3 py-2 bg-transparent text-sm" placeholder="2026" data-testid="filter-annee" />
        </div>
      </div>

      {/* List */}
      {loading ? (
        <div className="p-12 label-mono text-center">Chargement...</div>
      ) : reports.length === 0 ? (
        <div className="border border-black/15 p-16 text-center bg-[var(--paper-2)]">
          <Globe size={56} weight="duotone" className="mx-auto text-[var(--signal)]" />
          <h2 className="mt-4 font-serif text-3xl">Aucun rapport importé</h2>
          <p className="mt-3 text-[var(--muted)] max-w-xl mx-auto">Importez votre premier rapport flagship (FMI WEO, BAD AEO, BM IDR...) pour activer l'analyse IA et le moteur de comparaison.</p>
          <button onClick={() => setShowUpload(true)} data-testid="obs-empty-cta"
            className="inline-flex mt-8 bg-[var(--signal)] hover:bg-[var(--signal-hover)] text-[var(--paper)] px-6 py-3 label-mono items-center gap-2">
            <UploadSimple size={14} weight="bold" /> Importer le premier <ArrowRight size={14} />
          </button>
        </div>
      ) : (
        <div className="border border-black/15">
          <table className="w-full">
            <thead className="border-b border-black/15 label-mono text-left">
              <tr>
                <th className="p-4 w-10"></th>
                <th className="p-4">Titre</th>
                <th className="p-4">Organisation</th>
                <th className="p-4">Type</th>
                <th className="p-4">Pays</th>
                <th className="p-4">Année</th>
                <th className="p-4">Statut</th>
                <th className="p-4"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-black/10">
              {reports.map((r) => (
                <tr key={r.id} className="hover:bg-[var(--paper-2)]" data-testid={`obs-row-${r.id}`}>
                  <td className="p-4">
                    {r.analyse && (
                      <button onClick={() => toggleSelect(r.id)} data-testid={`obs-select-${r.id}`}
                        className={`w-5 h-5 border border-black flex items-center justify-center ${selected.has(r.id) ? "bg-[var(--signal)] text-[var(--paper)]" : ""}`}>
                        {selected.has(r.id) && <Check size={12} weight="bold" />}
                      </button>
                    )}
                  </td>
                  <td className="p-4 font-serif">
                    <Link to={`/observatory/reports/${r.id}`} className="hover:text-[var(--signal)]">{r.titre}</Link>
                  </td>
                  <td className="p-4">
                    <span className="font-mono text-xs px-2 py-0.5 border" style={{ borderColor: taxo.organisations[r.organisation]?.couleur, color: taxo.organisations[r.organisation]?.couleur }}>
                      {taxo.organisations[r.organisation]?.label || r.organisation}
                    </span>
                  </td>
                  <td className="p-4 font-mono text-xs">{r.type_rapport}</td>
                  <td className="p-4 font-mono text-xs">{taxo.pays.find((p) => p.code === r.pays)?.nom || r.pays}</td>
                  <td className="p-4 tabular-nums">{r.annee}</td>
                  <td className="p-4">
                    {r.analyse ? (
                      <span className="font-mono text-xs text-[var(--success)]">✓ Analysé</span>
                    ) : (
                      <button onClick={() => analyze(r.id)} className="text-[var(--signal)] label-mono hover:underline flex items-center gap-1">
                        <Sparkle size={12} weight="fill" /> Analyser
                      </button>
                    )}
                  </td>
                  <td className="p-4 text-right">
                    <button onClick={() => remove(r.id)} className="text-[var(--muted)] hover:text-[var(--signal)]">
                      <Trash size={14} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showUpload && <UploadDialog taxo={taxo} onClose={() => setShowUpload(false)} onDone={() => { setShowUpload(false); load(); }} />}
    </div>
  );
}

function Filter({ label, value, options, onChange, testid }) {
  return (
    <div>
      <div className="label-mono mb-1">{label}</div>
      <select value={value} onChange={(e) => onChange(e.target.value)}
        className="w-full border border-black/30 px-3 py-2 bg-transparent text-sm" data-testid={testid}>
        <option value="">Tous</option>
        {options.map(([k, v]) => <option key={k} value={k}>{v}</option>)}
      </select>
    </div>
  );
}

function UploadDialog({ taxo, onClose, onDone }) {
  const fileRef = useRef(null);
  const [tab, setTab] = useState("file");
  const [file, setFile] = useState(null);
  const [url, setUrl] = useState("");
  const [meta, setMeta] = useState({
    organisation: "FMI", type_rapport: "WEO", cote: "FINAL",
    pays: "AFRIQUE", annee: 2026, theme: "dette", titre: "", source_url: "",
  });
  const [uploading, setUploading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setUploading(true);
    try {
      if (tab === "file") {
        if (!file) return toast.error("Sélectionnez un PDF");
        const fd = new FormData();
        fd.append("file", file);
        await api.post("/observatory/reports/upload", fd, {
          headers: { "Content-Type": "multipart/form-data" },
          params: meta,
        });
        toast.success("Rapport importé");
      } else {
        if (!url.match(/^https?:\/\//)) return toast.error("URL http(s) requise");
        toast.info("Téléchargement depuis la source...");
        await api.post("/observatory/reports/fetch_url", { ...meta, url });
        toast.success("Rapport téléchargé et ingéré");
      }
      onDone();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur");
    } finally { setUploading(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={onClose} data-testid="obs-upload-dialog">
      <div className="bg-[var(--paper)] border border-black max-w-2xl w-full max-h-[90vh] overflow-auto" onClick={(e) => e.stopPropagation()}>
        <form onSubmit={submit}>
          <div className="border-b border-black p-6 flex items-center justify-between">
            <div>
              <div className="label-mono">§ Importation</div>
              <h2 className="font-serif text-2xl mt-1">Nouveau rapport flagship</h2>
            </div>
            <button type="button" onClick={onClose} className="label-mono">× Fermer</button>
          </div>
          <div className="p-6 space-y-4">
            {/* Tabs */}
            <div className="flex border-b border-black/15 -mt-2 mb-4">
              <button type="button" onClick={() => setTab("file")} data-testid="tab-file"
                className={`px-4 py-2 label-mono border-b-2 ${tab === "file" ? "border-[var(--signal)] text-[var(--signal)]" : "border-transparent text-[var(--muted)]"}`}>
                Fichier local
              </button>
              <button type="button" onClick={() => setTab("url")} data-testid="tab-url"
                className={`px-4 py-2 label-mono border-b-2 ${tab === "url" ? "border-[var(--signal)] text-[var(--signal)]" : "border-transparent text-[var(--muted)]"}`}>
                Depuis URL
              </button>
            </div>

            {tab === "file" ? (
              <div className="border-2 border-dashed border-black/30 p-6 text-center bg-[var(--paper-2)]">
                <input ref={fileRef} type="file" accept="application/pdf" onChange={(e) => setFile(e.target.files?.[0] || null)}
                  className="hidden" data-testid="obs-file-input" />
                <button type="button" onClick={() => fileRef.current?.click()} className="border border-black px-4 py-2 label-mono hover:bg-black hover:text-[var(--paper)]">
                  {file ? `📄 ${file.name}` : "Choisir un PDF"}
                </button>
                {file && <div className="mt-2 font-mono text-xs text-[var(--muted)]">{(file.size/1024).toFixed(0)} Ko</div>}
              </div>
            ) : (
              <Field label="URL publique du PDF">
                <input type="url" value={url} onChange={(e) => setUrl(e.target.value)} required
                  className="w-full border border-black px-3 py-2 bg-transparent font-mono text-sm" placeholder="https://www.imf.org/.../weo-2026.pdf" data-testid="obs-url-input" />
                <div className="mt-1 text-xs text-[var(--muted)] font-mono">Le serveur télécharge le PDF directement depuis la source.</div>
              </Field>
            )}

            <Field label="Titre">
              <input value={meta.titre} onChange={(e) => setMeta({...meta, titre: e.target.value})}
                className="w-full border border-black px-3 py-2 bg-transparent" placeholder="Ex: WEO Octobre 2026 — Afrique sub-saharienne" data-testid="obs-titre" />
            </Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Organisation">
                <select value={meta.organisation} onChange={(e) => setMeta({...meta, organisation: e.target.value})}
                  className="w-full border border-black px-3 py-2 bg-transparent" data-testid="obs-org">
                  {Object.entries(taxo.organisations).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                </select>
              </Field>
              <Field label="Type de rapport">
                <select value={meta.type_rapport} onChange={(e) => setMeta({...meta, type_rapport: e.target.value})}
                  className="w-full border border-black px-3 py-2 bg-transparent" data-testid="obs-type">
                  {Object.entries(taxo.types_rapport).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                </select>
              </Field>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <Field label="Pays / Région">
                <select value={meta.pays} onChange={(e) => setMeta({...meta, pays: e.target.value})}
                  className="w-full border border-black px-3 py-2 bg-transparent" data-testid="obs-pays">
                  {taxo.pays.map((p) => <option key={p.code} value={p.code}>{p.nom}</option>)}
                </select>
              </Field>
              <Field label="Année">
                <input type="number" value={meta.annee} onChange={(e) => setMeta({...meta, annee: e.target.value})}
                  className="w-full border border-black px-3 py-2 bg-transparent" data-testid="obs-annee" />
              </Field>
              <Field label="Cote">
                <select value={meta.cote} onChange={(e) => setMeta({...meta, cote: e.target.value})}
                  className="w-full border border-black px-3 py-2 bg-transparent" data-testid="obs-cote">
                  {Object.entries(taxo.cotes).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                </select>
              </Field>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Thème">
                <select value={meta.theme} onChange={(e) => setMeta({...meta, theme: e.target.value})}
                  className="w-full border border-black px-3 py-2 bg-transparent" data-testid="obs-theme">
                  {taxo.themes.map((t) => <option key={t.code} value={t.code}>{t.label}</option>)}
                </select>
              </Field>
              <Field label="URL source (optionnel)">
                <input value={meta.source_url} onChange={(e) => setMeta({...meta, source_url: e.target.value})}
                  className="w-full border border-black px-3 py-2 bg-transparent" placeholder="https://..." data-testid="obs-url" />
              </Field>
            </div>
          </div>
          <div className="border-t border-black p-6 flex justify-end gap-2">
            <button type="button" onClick={onClose} className="border border-black px-4 py-2 label-mono">Annuler</button>
            <button type="submit" disabled={uploading || (tab === "file" && !file) || (tab === "url" && !url)} data-testid="obs-upload-submit"
              className="bg-[var(--signal)] hover:bg-[var(--signal-hover)] text-[var(--paper)] px-5 py-2 label-mono disabled:opacity-50">
              {uploading ? (tab === "url" ? "Téléchargement..." : "Import en cours...") : (tab === "url" ? "Télécharger depuis l'URL" : "Importer le rapport")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <div className="label-mono mb-1">{label}</div>
      {children}
    </label>
  );
}
